import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { Cell, Column, SortingRule } from 'react-table'
import { useIntl } from 'react-intl'

import { useAppDispatch, useAppSelector } from 'app/hooks'
import { commonMessages } from 'app/i18n/messages'

import { CurrentUserPermissionsContext } from 'features/auth/permissions/CurrentUserPermissionsContext'
import {
  currentRootDomainIdSelector,
  selectYouAreDistributor,
} from 'features/rootDomains/selectors'
import { LicensesContext } from 'features/licenses/LicensesContext'
import { importLicenseActions } from 'features/licenses/slice'
import type { LicenseType } from 'features/licenses/types'
import { useEmptyGroups } from 'features/licenses/hooks'
import { getSortOptions, sortByDate } from 'components/Table/utils'
import { DateCell } from 'components/Table'
import LicenseStatuses from '../LicenseStatuses'
import LicensesAvailableCodesMobile from './LicensesAvailableCodesMobile'
import LicensesUsedCodesMobile from './LicensesUsedCodesMobile'
import LicensesGroup from './LicensesGroup'
import CopyCode from './CopyCode'
import Note from './Note'
import { LicenseCellStyles, StyledNoLicenses } from './styles'
import LicenseRowActions from './LicenseRowActions'
import LicenseDomainName from './LicenseDomainName'
import LicensesHomeLinkCell from './LicensesHomeLinkCell'
import ReclaimLicenseModal from '../ReclaimLicenseModal'
import TransferLicenseModal from '../TransferLicenseModal'

interface LicensesGroupsTableProps {
  licensesGroups: LicenseType[][]
  searchValue?: string
  isAppliedLicensesFilters: boolean
  showAvailableCodes: boolean
}

const DEFAULT_SORT = {
  id: 'code',
  desc: true,
}

function LicensesGroupsTable(props: LicensesGroupsTableProps) {
  const { formatMessage } = useIntl()
  const dispatch = useAppDispatch()
  const currentRootId = useAppSelector(currentRootDomainIdSelector)
  const youAreDistributor = useAppSelector(selectYouAreDistributor)

  const { licensesGroups, searchValue, showAvailableCodes } = props

  const { hasRootUserPermission } = useContext(CurrentUserPermissionsContext)

  const [selectedLicense, setSelectedLicense] = useState<LicenseType | null>(
    null
  )
  const [isOpenReclaimModal, setIsOpenReclaimModal] = useState(false)
  const [isOpenTransferModal, setIsOpenTransferModal] = useState(false)

  const hiddenColumns = useMemo(() => {
    const hiddenColumnsIds = []
    if (!hasRootUserPermission) hiddenColumnsIds.push('homeId')
    if (!youAreDistributor) hiddenColumnsIds.push('rowActions', 'domainName')
    return hiddenColumnsIds
  }, [hasRootUserPermission, youAreDistributor])

  const [emptyGroups, addEmptyGroup, removeEmptyGroup] = useEmptyGroups()

  const AvailableCodeInfo = useCallback(
    (cell: Cell) => {
      return (
        <div>
          <CopyCode code={cell.value} />
          <LicenseStatuses
            license={cell.row.original}
            rootDomainId={currentRootId}
          />
        </div>
      )
    },
    [currentRootId]
  )
  const UsedCodeInfo = useCallback(
    (cell: Cell) => (
      <div>
        <strong>{cell.value}</strong>
        <LicenseStatuses
          license={cell.row.original}
          rootDomainId={currentRootId}
        />
      </div>
    ),
    [currentRootId]
  )

  const availableCodesColumns: readonly Column<LicenseType>[] = useMemo(
    () => [
      {
        Header: 'License',
        accessor: 'code',
        Cell: AvailableCodeInfo,
        percentWidth: '20%',
        cellStyles: LicenseCellStyles,
      },
      {
        Header: 'Order Number',
        accessor: 'orderNumber',
        cellStyles: LicenseCellStyles,
      },
      {
        Header: 'Purchase Order',
        accessor: 'customerPO',
        disableGlobalFilter: true,
        cellStyles: LicenseCellStyles,
      },
      {
        Header: 'Created',
        accessor: 'created',
        Cell: DateCell,
        sortType: sortByDate,
        disableGlobalFilter: true,
        cellStyles: LicenseCellStyles,
      },
      {
        Header: 'Domain',
        accessor: 'domainName',
        disableGlobalFilter: true,
        cellStyles: LicenseCellStyles,
      },
      {
        Header: 'Notes',
        accessor: 'notes',
        Cell: (cell: Cell<LicenseType>) => (
          <Note original={cell.row.original} />
        ),
        cellStyles: LicenseCellStyles,
      },
      {
        Header: '',
        id: 'rowActions',
        Cell: (cell: Cell<LicenseType>) => (
          <LicenseRowActions
            license={cell.row.original}
            rootDomainId={currentRootId}
            onClickToReclaim={() => {
              setSelectedLicense(cell.row.original)
              setIsOpenReclaimModal(true)
            }}
            onClickToTransfer={() => {
              setSelectedLicense(cell.row.original)
              setIsOpenTransferModal(true)
            }}
          />
        ),
      },
    ],
    [AvailableCodeInfo, currentRootId]
  )

  const usedCodesColumns: readonly Column<LicenseType>[] = useMemo(
    () => [
      {
        Header: 'License',
        accessor: 'code',
        Cell: UsedCodeInfo,
        percentWidth: '15%',
        cellStyles: LicenseCellStyles,
      },
      {
        Header: 'Savant Order',
        accessor: 'orderNumber',
        percentWidth: '15%',
        cellStyles: LicenseCellStyles,
      },
      {
        Header: 'Purchase Order',
        accessor: 'customerPO',
        percentWidth: '15%',
        disableGlobalFilter: true,
        cellStyles: LicenseCellStyles,
      },
      {
        Header: 'Domain',
        id: 'domainName',
        disableGlobalFilter: true,
        Cell: (cell: Cell<LicenseType>) => (
          <LicenseDomainName
            license={cell.row.original}
            rootDomainId={currentRootId}
            youAreDistributor={youAreDistributor}
          />
        ),
        cellStyles: LicenseCellStyles,
      },
      {
        Header: 'Home',
        accessor: 'homeId',
        Cell: (cell: Cell<LicenseType>) => (
          <LicensesHomeLinkCell
            homeId={cell.row.original.homeId}
            homeName={cell.row.original.homeName}
            homeRootDomainId={cell.row.original.homeRootDomainId || ''}
          />
        ),
        percentWidth: '15%',
        disableGlobalFilter: true,
        cellStyles: LicenseCellStyles,
      },
      {
        Header: 'Activated',
        accessor: 'activated',
        percentWidth: '18%',
        Cell: DateCell,
        sortType: sortByDate,
        disableGlobalFilter: true,
        cellStyles: LicenseCellStyles,
      },
      {
        Header: 'Notes',
        accessor: 'notes',
        percentWidth: '22%',
        Cell: (cell: Cell<LicenseType>) => (
          <Note original={cell.row.original} />
        ),
        cellStyles: LicenseCellStyles,
      },
    ],
    [UsedCodeInfo, currentRootId, youAreDistributor]
  )

  const columns = useMemo(
    () => (showAvailableCodes ? availableCodesColumns : usedCodesColumns),
    [availableCodesColumns, showAvailableCodes, usedCodesColumns]
  )
  const mobileViewComponent = useMemo(
    () =>
      showAvailableCodes
        ? LicensesAvailableCodesMobile
        : LicensesUsedCodesMobile,
    [showAvailableCodes]
  )

  const noSearchResults = emptyGroups.length === licensesGroups.length

  const availableCodesHeaders = useMemo(
    () => ({
      code: {
        label: 'License',
        id: 'code',
      },
      orderNumber: {
        label: 'Savant Order',
        id: 'orderNumber',
      },
      customerPO: {
        label: 'Purchase Order',
        id: 'customerPO',
      },
      created: {
        label: 'Created',
        id: 'created',
      },
    }),
    []
  )

  const usedCodesHeaders = useMemo(
    () => ({
      code: {
        label: 'License',
        id: 'code',
      },
      orderNumber: {
        label: 'Savant Order',
        id: 'orderNumber',
      },
      customerPO: {
        label: 'Purchase Order',
        id: 'customerPO',
      },
      homeId: {
        label: 'Home',
        id: 'homeId',
      },
      activated: {
        label: 'Activated',
        id: 'activated',
      },
    }),
    []
  )

  const sortOptions = useMemo(
    () =>
      showAvailableCodes
        ? getSortOptions(availableCodesHeaders)
        : getSortOptions(usedCodesHeaders),

    [availableCodesHeaders, usedCodesHeaders, showAvailableCodes]
  )

  const chosenHeaders = useMemo(
    () => (showAvailableCodes ? availableCodesHeaders : usedCodesHeaders),
    [availableCodesHeaders, showAvailableCodes, usedCodesHeaders]
  )

  const [sortBy, setSortBy] = useState<SortingRule<LicenseType>>(DEFAULT_SORT)

  useEffect(() => {
    return () => {
      dispatch(importLicenseActions.clear())
    }
  }, [dispatch])

  return (
    <LicensesContext.Provider
      value={{
        setSelectedLicense,
        setIsOpenReclaimModal,
        setIsOpenTransferModal,
      }}
    >
      {youAreDistributor && (
        <>
          <ReclaimLicenseModal
            isOpen={isOpenReclaimModal}
            handleClose={() => window.location.reload(false)}
            license={selectedLicense}
          />
          <TransferLicenseModal
            isOpen={isOpenTransferModal}
            handleClose={() => window.location.reload(false)}
            license={selectedLicense}
          />
        </>
      )}
      {noSearchResults && (
        <StyledNoLicenses>
          {formatMessage(commonMessages.noResultsFound)}
        </StyledNoLicenses>
      )}
      {licensesGroups.map((licensesGroup) => (
        <LicensesGroup
          key={licensesGroup[0].id}
          columns={columns}
          data={licensesGroup}
          searchValue={searchValue}
          addEmptyGroup={addEmptyGroup}
          removeEmptyGroup={removeEmptyGroup}
          defaultSortBy={DEFAULT_SORT}
          hiddenColumns={hiddenColumns}
          mobileViewComponent={mobileViewComponent}
          sortOptions={sortOptions}
          headers={chosenHeaders}
          sortBy={sortBy}
          setSortBy={setSortBy}
          showAvailableCodes={showAvailableCodes}
        />
      ))}
    </LicensesContext.Provider>
  )
}

export default memo(LicensesGroupsTable)
